import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { fetchStars, fetchWhitelist } from 'api/galaxyApi';
import { IS_DEV } from 'consts';
import { GALAXY_CONFIG } from 'consts/copyTrading';
import { EngineStar, SpaceEngine } from 'engine';
import { reaction } from 'mobx';
import { store } from 'store';
import { processStarsFromApi } from 'utils/galaxy';
import { mindPositionFor, outerRingPositionFor } from 'utils/galaxy/viewModeCalculators/mind';

import * as S from './GalaxyCanvas.styles';

/**
 * Thin React bridge: MobX stores ↔ SpaceEngine.
 * No Three.js knowledge. ~80 lines.
 */
export const GalaxyCanvas: FC = observer(() => {
  const { setMode, resetMode } = store.commonStore;
  const { setLoading, updateLoadingProgress, updateViewTransition } = store.galaxyStore;
  const { setCurrentBlock } = store.web3Store;
  const { openTraderCard, closeTraderCard, setTargetFrame } = store.galaxyStore;
  const { addScannedWallets } = store.galaxyStore;
  const { consumePendingEffects, traderIndexByAddress } = store.galaxyStore;

  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const engineRef = useRef<SpaceEngine | null>(null);

  // Create engine when container is available
  useEffect(() => {
    if (!container) return;
    const { mode } = store.commonStore;

    const engine = new SpaceEngine(container, GALAXY_CONFIG);
    engineRef.current = engine;

    // --- Engine events → MobX ---
    engine.on('star:click', (_wallet, index, x, y) => {
      if (mode === 'autopilot') return;
      resetMode();
      openTraderCard(index);
      // Target frame appears at click position and tracks the star indefinitely
      // until the trader card is closed (selectedTraderIndex reaction below).
      setTargetFrame(true, x, y);
      engine.trackStar(
        index,
        Infinity,
        (nx, ny) => setTargetFrame(true, nx, ny),
        () => {}
      );
    });

    engine.on('ship:click', () => {
      const { mode } = store.commonStore;
      if (mode === 'main') {
        setMode('pilot');
      }
    });

    // Wire scan complete → MobX wallets
    engine.setScanCompleteCallback((wallets) => {
      addScannedWallets(wallets);
    });

    engine.on('portal:click', (distance) => {
      if (distance > 120) {
        engine.flyToPortal();
      } else {
        engine.startWormholeTravel();
      }
    });

    engine.on('portal:arrived', () => {
      setMode('portal');
    });

    engine.on('portal:returned', () => {
      // Return to 'main' mode so galaxy UI (ViewSelector, EventFeed,
      // TraderDetailPanel) re-activates and the Galaxy menu button lights up.
      resetMode();
    });

    engine.on('swap:enter', () => {
      setMode('swap');
    });

    engine.on('swap:exit', () => {
      if (store.commonStore.mode === 'swap') {
        resetMode();
      }
    });

    engine.on('empty:click', () => {
      // Single click on empty space — close panel + stop target frame (matches original closeDetail)
      closeTraderCard();
      engine.stopStarTracking();
      setTargetFrame(false);
    });

    return () => {
      engine.destroy();
      engineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container]);

  // --- MobX reactions → Engine ---

  // Star tracking: start when trader selected (EventFeed click), stop when closed
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    const dispose = reaction(
      () => store.galaxyStore.selectedTraderIndex,
      (index) => {
        if (index === null) {
          engine.stopStarTracking();
          setTargetFrame(false);
        } else {
          // Start tracking so target frame follows the star (no camera zoom)
          engine.trackStar(
            index,
            Infinity,
            (x, y) => setTargetFrame(true, x, y),
            () => {}
          );
        }
      }
    );
    return () => dispose();
  }, [container, setTargetFrame]);

  // View mode changes
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    let rafId = 0;
    let lastTime = 0;

    const dispose = reaction(
      () => store.galaxyStore.currentViewMode,
      (mode) => {
        engine.switchView(mode);

        // Drive store transition progress for ViewTransitionBar
        cancelAnimationFrame(rafId);
        lastTime = performance.now();
        const tick = (now: number) => {
          const dt = (now - lastTime) / 1000;
          lastTime = now;
          updateViewTransition(dt);
          if (store.galaxyStore.viewTransition.active) {
            rafId = requestAnimationFrame(tick);
          }
        };
        rafId = requestAnimationFrame(tick);
      }
    );
    return () => {
      dispose();
      cancelAnimationFrame(rafId);
    };
  }, [container, updateViewTransition]);

  // Sophie/UI: fly camera to a star (seq bumps allow repeated flights)
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    const dispose = reaction(
      () => store.galaxyStore.flyToStarSeq,
      () => {
        const index = store.galaxyStore.flyToStarIndex;
        if (index !== null && index >= 0) {
          engine.flyToStar(index);
        }
      }
    );
    return () => dispose();
  }, [container]);

  // Sophie/UI: highlight a subset of stars by address, dim the rest
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    const dispose = reaction(
      () => store.galaxyStore.highlightSeq,
      () => {
        const addrs = store.galaxyStore.highlightAddresses;
        if (!addrs || addrs.length === 0) {
          engine.setHighlight(null);
          return;
        }
        const map = store.galaxyStore.traderIndexByAddress;
        const indices: number[] = [];
        for (const a of addrs) {
          const idx = map.get(a.toLowerCase());
          if (idx !== undefined) indices.push(idx);
        }
        engine.setHighlight(indices.length > 0 ? indices : null);
      }
    );
    return () => dispose();
  }, [container]);

  // Search: guest star for wallets outside the displayed set — pulsing
  // marker at their behavioral-map spot + camera flight
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    const dispose = reaction(
      () => store.galaxyStore.guestSeq,
      () => {
        const guest = store.galaxyStore.guestStar;
        if (!guest) {
          engine.clearSearchMarker();
          return;
        }
        const pos = guest.mind
          ? mindPositionFor(guest.mind, GALAXY_CONFIG)
          : outerRingPositionFor(guest.wallet, GALAXY_CONFIG);
        engine.setSearchMarker(pos);
        engine.flyToMapPoint(pos, 30);
      }
    );
    return () => dispose();
  }, [container]);

  // Swap mode
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    const dispose = reaction(
      () => store.commonStore.mode === 'swap',
      (active) => {
        if (active) engine.enterSwapMode();
        else engine.exitSwapMode();
      }
    );
    return () => dispose();
  }, [container]);

  // Portal flight
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    const dispose = reaction(
      () => store.galaxyStore.portalFlightRequest,
      () => {
        if (store.commonStore.mode === 'portal') {
          engine.flyBack();
        } else {
          engine.flyToPortal();
        }
      }
    );
    return () => dispose();
  }, [container]);

  // Wormhole travel
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    const dispose = reaction(
      () => store.galaxyStore.wormholeTravelRequest,
      () => {
        engine.startWormholeTravel();
      }
    );
    return () => dispose();
  }, [container]);

  // Ship mode (pilot / autopilot / orbit) — single reaction covers follow + autopilot
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    const dispose = reaction(
      () => store.commonStore.mode,
      (mode) => {
        if (mode === 'pilot') engine.setShipMode('pilot');
        else if (mode === 'autopilot') engine.setShipMode('autopilot');
        else engine.resetShipMode();
      }
    );
    return () => dispose();
  }, [container]);

  // Pilot mode — smooth (dt-normalised) galaxy rotation
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    const dispose = reaction(
      () => store.commonStore.mode,
      (mode) => {
        engine.setPilotMode(mode === 'pilot');
      }
    );
    return () => dispose();
  }, [container]);

  // Alien view
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    const dispose = reaction(
      () => store.commonStore.mode === 'assistant',
      (active) => {
        if (active) engine.enterAlienView();
        else engine.exitAlienView();
      }
    );
    return () => dispose();
  }, [container]);

  // Trade effects
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    const dispose = reaction(
      () => store.galaxyStore.pendingEffects.length,
      (len) => {
        if (len === 0) return;
        const effects = consumePendingEffects();
        for (const e of effects) {
          engine.triggerEffect(e.traderIndex, e.type, e.intensity);
        }
      }
    );
    return () => dispose();
  }, [container, consumePendingEffects]);

  // --- Data loading ---
  const loadStars = useCallback(async () => {
    const engine = engineRef.current;
    if (!engine) return;

    try {
      setLoading(true);
      updateLoadingProgress('Connecting to API...', 10);

      const [response, whitelist] = await Promise.all([
        fetchStars(IS_DEV ? 5 : 5_000),
        fetchWhitelist(),
      ]);
      updateLoadingProgress('Downloading trader data...', 30);

      const totalTraders = response.stars.length;

      // Count-up animation
      const increment = Math.ceil(totalTraders / 20);
      let displayCount = 0;
      await new Promise<void>((resolve) => {
        const countUp = () => {
          displayCount = Math.min(displayCount + increment, totalTraders);
          const percent = 30 + (displayCount / totalTraders) * 60;
          updateLoadingProgress('Loading traders...', percent, displayCount);
          if (displayCount < totalTraders) {
            setTimeout(countUp, 30);
          } else {
            updateLoadingProgress('Initializing galaxy...', 95, totalTraders);
            resolve();
          }
        };
        countUp();
      });

      const processedStars = processStarsFromApi(response.stars, GALAXY_CONFIG);

      // Pass to engine
      engine.setStars(processedStars as unknown as EngineStar[]);

      // Constellation labels: centroid of each named cluster among the
      // displayed stars (min 12 visible members, top 24 by size)
      const clusterAgg = new Map<
        number,
        { name: string | null; n: number; sx: number; sy: number; sz: number }
      >();
      for (const s of processedStars) {
        const m = s.mind;
        if (!m || m.cluster === null) continue;
        const p = mindPositionFor(m, GALAXY_CONFIG);
        const agg = clusterAgg.get(m.cluster) ?? { name: null, n: 0, sx: 0, sy: 0, sz: 0 };
        agg.n += 1;
        agg.sx += p.x;
        agg.sy += p.y;
        agg.sz += p.z;
        if (!agg.name && m.archetype) agg.name = m.archetype;
        clusterAgg.set(m.cluster, agg);
      }
      const mindLabels = [...clusterAgg.values()]
        .filter((a) => a.name !== null && a.n >= 15)
        .sort((a, b) => b.n - a.n)
        .slice(0, 18)
        .map((a, rank) => ({
          name: a.name as string,
          count: a.n,
          // Alternate label heights so neighbouring constellations don't overlap
          position: { x: a.sx / a.n, y: a.sy / a.n + (rank % 2 === 0 ? 3.5 : 9), z: a.sz / a.n },
        }));
      engine.setMindLabels(mindLabels);

      // Feed the autopilot scan pool. Curated whitelist wins; if it's
      // empty (API down or returned nothing) we fall back to the wider
      // stars dataset, which is still strictly better than random hex.
      if (whitelist.length > 0) {
        engine.setKnownWallets(whitelist.map((t) => ({ address: t.wallet, roi: t.roi })));
      } else {
        console.warn(
          '[GalaxyCanvas] /api/whitelist returned empty; falling back to /stars dataset for autopilot scan'
        );
        engine.setKnownWallets(processedStars.map((s) => ({ address: s.wallet, roi: s.f_roi })));
      }

      // Sync to MobX stores
      setCurrentBlock(response.current_block);
      store.galaxyStore.setMindAvailable(processedStars.some((s) => s.mind !== null));
      store.galaxyStore.traders = processedStars;
      traderIndexByAddress.clear();
      processedStars.forEach((star, index) => {
        traderIndexByAddress.set(star.wallet.toLowerCase(), index);
      });

      updateLoadingProgress('Galaxy ready!', 100, totalTraders);
      await new Promise((r) => setTimeout(r, 40));
    } catch (error) {
      console.error('Failed to load stars:', error);
      updateLoadingProgress('API unavailable - using demo data', 50);
      await new Promise((r) => setTimeout(r, 50));
    } finally {
      setLoading(false);
    }
  }, [setLoading, setCurrentBlock, traderIndexByAddress, updateLoadingProgress]);

  useEffect(() => {
    if (engineRef.current) {
      loadStars();
    }
  }, [container, loadStars]);

  return <S.Container ref={setContainer} />;
});
